import type { User, AdminUser, DashboardStats, PlatformStats, AdEvent, WireGuardConfig, AdminStats, RegistrationCode, LifecycleSummary, ClassifierOverride, CapacityHistory, VpnStatus, SystemOverview, SystemOverviewHistory, SystemHealth, EmailTemplateSettings, QuicBlockingStatus, MemberLoginHistory, ResearchOverview, GamblingExposure, LegalStatusDaily, TemporalHeatmapCell, PlatformComparison, AdvertiserProfile, ExposureDistribution, CategoryCooccurrence, WeeklyTrend, ParticipationData, MemberDemographics, ActivityTimeline, DailySummaryReport, TesterAccessReport, PsaOverview, PsaCategory, PsaTemporal, PsaDailyPoint, PsaDemographics, PsaAdvertiser, PsaReviewItem, PsaSourceResult, PsaRun, PsaNo4PerMember, PsaNo4TypeMix, PsaNo4Parity, PsaReport0807, PsaReport0807Parity, TestPeriod } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '';

async function fetchAPI(url: string, options: RequestInit = {}) {
  // Add cache busting for GET requests
  const cacheBuster = options.method === 'GET' || !options.method
    ? (url.includes('?') ? '&' : '?') + '_t=' + Date.now()
    : '';

  const response = await fetch(`${API_BASE}${url}${cacheBuster}`, {
    ...options,
    credentials: 'include',
    cache: 'no-store', // Prevent browser caching
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Auth APIs
export const authAPI = {
  memberLogin: (code: string) => fetchAPI('/api/auth/member/login', {
    method: 'POST',
    body: JSON.stringify({ code }),
  }),

  adminLogin: (username: string, password: string) => fetchAPI('/api/auth/admin/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  }),

  memberLogout: () => fetchAPI('/api/auth/member/logout', { method: 'POST' }),
  adminLogout: () => fetchAPI('/api/auth/admin/logout', { method: 'POST' }),

  checkMemberAuth: (): Promise<{ authenticated: boolean; isDemo?: boolean; user?: User; vpn?: VpnStatus }> =>
    fetchAPI('/api/auth/member/check'),

  checkAdminAuth: (): Promise<{ authenticated: boolean; user?: AdminUser }> =>
    fetchAPI('/api/auth/admin/check'),

  changeAdminPassword: (oldPassword: string, newPassword: string): Promise<{ success: boolean }> =>
    fetchAPI('/api/auth/admin/change-password', {
      method: 'POST',
      body: JSON.stringify({ oldPassword, newPassword }),
    }),
};

// Admin user management (sasadmin only).
export interface AdminUserRow {
  id: number;
  username: string;
  email: string;
  name: string;
  role: 'sasadmin' | 'sys-operator';
  enabled: boolean;
  createdAt: string;
  lastLogin: string | null;
}

export const adminUsersAPI = {
  list: (): Promise<{ users: AdminUserRow[] }> =>
    fetchAPI('/api/admin/admin-users'),

  create: (input: {
    email: string;
    name: string;
    role: 'sasadmin' | 'sys-operator';
    password?: string;
    username?: string;
  }): Promise<{ success: boolean; user: AdminUserRow }> =>
    fetchAPI('/api/admin/admin-users', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  update: (id: number, input: {
    name?: string;
    role?: 'sasadmin' | 'sys-operator';
    enabled?: boolean;
  }): Promise<{ success: boolean; user: AdminUserRow }> =>
    fetchAPI(`/api/admin/admin-users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  resetPassword: (id: number, password?: string): Promise<{ success: boolean }> =>
    fetchAPI(`/api/admin/admin-users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify(password ? { password } : {}),
    }),
};

// Member APIs
export const memberAPI = {
  getStats: (): Promise<DashboardStats> => fetchAPI('/api/member/stats'),

  getPlatformStats: (): Promise<PlatformStats[]> => fetchAPI('/api/member/platform-stats'),

  getRecentAds: (limit: number = 10): Promise<AdEvent[]> =>
    fetchAPI(`/api/member/recent-ads?limit=${limit}`),

  getCategoryStats: (): Promise<any[]> =>
    fetchAPI('/api/member/category-stats'),

  getWireGuardConfig: (): Promise<WireGuardConfig> => fetchAPI('/api/member/wireguard-config'),

  getLatestApk: (): Promise<any> => fetchAPI('/api/member/apk/latest'),

  markApkDownloaded: (apkId: number): Promise<any> =>
    fetchAPI('/api/member/apk/mark-downloaded', {
      method: 'POST',
      body: JSON.stringify({ apkId }),
    }),

  hasApkUpdate: (): Promise<{ hasUpdate: boolean; version: string | null }> =>
    fetchAPI('/api/member/apk/has-update'),
};

// Admin APIs
export const adminAPI = {
  getStats: (): Promise<AdminStats> => fetchAPI('/api/admin/stats'),

  getCodes: (): Promise<RegistrationCode[]> => fetchAPI('/api/admin/codes'),

  getLifecycleSummary: (): Promise<LifecycleSummary> => fetchAPI('/api/admin/codes/lifecycle-summary'),

  // === AI Judge Overrides ===
  listClassifierOverrides: (): Promise<ClassifierOverride[]> =>
    fetchAPI('/api/admin/classifier-overrides'),
  upsertClassifierOverride: (data: { advertiserDomain: string; category: string; note?: string | null }): Promise<{ success: boolean; data: ClassifierOverride }> =>
    fetchAPI('/api/admin/classifier-overrides', { method: 'POST', body: JSON.stringify(data) }),
  patchClassifierOverride: (id: number, data: { enabled?: boolean; category?: string; note?: string }): Promise<{ success: boolean }> =>
    fetchAPI(`/api/admin/classifier-overrides/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteClassifierOverride: (id: number): Promise<{ success: boolean }> =>
    fetchAPI(`/api/admin/classifier-overrides/${id}`, { method: 'DELETE' }),
  bulkReclassifyByAdvertiser: (data: { advertiserDomain: string; advertiserName?: string | null; category: string; reason?: string; alsoSaveAsOverride?: boolean }): Promise<{ success: boolean; eventsUpdated: number; overrideId: number | null; overrideSkipped?: string | null }> =>
    fetchAPI('/api/admin/captured-ads/bulk-reclassify-by-advertiser', { method: 'POST', body: JSON.stringify(data) }),

  // === Capacity history ===
  getCapacityHistory: (period: '24h' | '7d' | '30d' = '24h'): Promise<CapacityHistory> =>
    fetchAPI(`/api/admin/capacity-history?period=${period}`),

  getCodesWithAdCounts: (): Promise<{ code: string; memberName: string | null; notes: string | null; adCount: number }[]> =>
    fetchAPI('/api/admin/codes/with-ad-counts'),

  generateCode: (opts?: { notes?: string; memberName?: string; memberEmail?: string }): Promise<{ code: string }> =>
    fetchAPI('/api/admin/codes/generate', {
      method: 'POST',
      body: JSON.stringify(opts || {}),
    }),

  bulkGenerateCodes: (input: {
    // New template-aware shape (preferred)
    fromIndex?: number;
    toIndex?: number;
    nameTemplate?: string;
    emailTemplate?: string;
    notesTemplate?: string;
    memberLanguage?: string;
    // Legacy shape (still supported)
    count?: number;
    notes?: string;
  }): Promise<{
    success: boolean;
    count?: number;
    completed?: number;
    failedAt?: number;
    error?: string;
    codes: {
      code: string; deviceGuid: string; vpnIp: string;
      memberName?: string | null; memberEmail?: string | null;
      notes?: string | null; memberLanguage?: string;
    }[];
    csv: string;
  }> =>
    fetchAPI('/api/admin/codes/bulk', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  revokeCode: (code: string): Promise<{ success: boolean }> =>
    fetchAPI('/api/admin/codes/revoke', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  deleteCode: (code: string): Promise<{ success: boolean }> =>
    fetchAPI(`/api/admin/codes/${code}`, {
      method: 'DELETE',
    }),

  updateCode: (code: string, data: { memberName?: string; memberEmail?: string; memberLanguage?: string; notes?: string }): Promise<{ success: boolean; data: RegistrationCode }> =>
    fetchAPI(`/api/admin/codes/${code}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getEmailTemplate: (): Promise<EmailTemplateSettings> =>
    fetchAPI('/api/admin/email-template'),

  updateEmailTemplate: (data: EmailTemplateSettings): Promise<{ success: boolean; data: EmailTemplateSettings }> =>
    fetchAPI('/api/admin/email-template', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getQuicBlocking: (): Promise<QuicBlockingStatus> =>
    fetchAPI('/api/admin/quic-blocking'),

  updateQuicBlocking: (enabled: boolean): Promise<{ success: boolean; enabled: boolean; message: string }> =>
    fetchAPI('/api/admin/quic-blocking', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  getCapturedAds: (platform: string = 'all', limit: number = 50, offset: number = 0, eventType?: string, category?: string, enrichmentStatus?: string, userCountry?: string, dateFrom?: string, dateTo?: string, viewMode?: string, showExcluded?: string, memberCodes?: string, sortBy?: string, sortOrder?: string, manuallyClassified?: string): Promise<any> => {
    let url = `/api/admin/captured-ads?platform=${platform}&limit=${limit}&offset=${offset}`;
    if (eventType && eventType !== 'all') {
      url += `&eventType=${eventType}`;
    }
    if (userCountry && userCountry !== 'all') {
      url += `&userCountry=${userCountry}`;
    }
    if (category && category !== 'all') {
      url += `&category=${category}`;
    }
    if (enrichmentStatus && enrichmentStatus !== 'all') {
      url += `&enrichmentStatus=${enrichmentStatus}`;
    }
    if (dateFrom) {
      url += `&dateFrom=${encodeURIComponent(dateFrom)}`;
    }
    if (dateTo) {
      url += `&dateTo=${encodeURIComponent(dateTo)}`;
    }
    if (viewMode && viewMode !== 'all') {
      url += `&viewMode=${viewMode}`;
    }
    if (showExcluded) {
      url += `&showExcluded=${showExcluded}`;
    }
    if (memberCodes) {
      url += `&memberCode=${encodeURIComponent(memberCodes)}`;
    }
    if (sortBy) {
      url += `&sortBy=${sortBy}`;
    }
    if (sortOrder) {
      url += `&sortOrder=${sortOrder}`;
    }
    if (manuallyClassified && manuallyClassified !== 'all') {
      url += `&manuallyClassified=${manuallyClassified}`;
    }
    return fetchAPI(url);
  },

  excludeAd: (id: number, reason?: string): Promise<any> =>
    fetchAPI(`/api/admin/captured-ads/${id}/exclude`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  restoreAd: (id: number): Promise<any> =>
    fetchAPI(`/api/admin/captured-ads/${id}/restore`, {
      method: 'POST',
    }),

  bulkExcludeAds: (ids: number[], reason?: string): Promise<any> =>
    fetchAPI('/api/admin/captured-ads/bulk-exclude', {
      method: 'POST',
      body: JSON.stringify({ ids, reason }),
    }),

  getExclusionRules: (): Promise<any> =>
    fetchAPI('/api/admin/exclusion-rules'),

  createExclusionRule: (rule: { rule_name: string; rule_type: string; pattern: string; reason: string }): Promise<any> =>
    fetchAPI('/api/admin/exclusion-rules', {
      method: 'POST',
      body: JSON.stringify(rule),
    }),

  applyExclusionRule: (id: number): Promise<any> =>
    fetchAPI(`/api/admin/exclusion-rules/${id}/apply`, {
      method: 'POST',
    }),

  getCapturedAdsStats: (opts?: { dateFrom?: string; dateTo?: string; userCountry?: string; memberCodes?: string }): Promise<any> => {
    const p = new URLSearchParams();
    if (opts?.dateFrom) p.set('dateFrom', opts.dateFrom);
    if (opts?.dateTo) p.set('dateTo', opts.dateTo);
    if (opts?.userCountry && opts.userCountry !== 'all') p.set('userCountry', opts.userCountry);
    if (opts?.memberCodes) p.set('memberCode', opts.memberCodes);
    const qs = p.toString();
    return fetchAPI(`/api/admin/captured-ads/stats${qs ? `?${qs}` : ''}`);
  },

  reclassifyAd: (id: number, category: string, reason?: string): Promise<any> =>
    fetchAPI(`/api/admin/captured-ads/${id}/reclassify`, {
      method: 'POST',
      body: JSON.stringify({ category, reason }),
    }),

  getClassificationHistory: (id: number): Promise<any[]> =>
    fetchAPI(`/api/admin/captured-ads/${id}/classification-history`),

  exportCapturedAdsUrl: (opts?: {
    platform?: string; dateFrom?: string; dateTo?: string;
    category?: string; eventType?: string; enrichmentStatus?: string;
    userCountry?: string; memberCodes?: string; showExcluded?: string;
  }): string => {
    const p = new URLSearchParams();
    p.set('format', 'csv');
    if (opts?.platform && opts.platform !== 'all') p.set('platform', opts.platform);
    if (opts?.dateFrom) p.set('dateFrom', opts.dateFrom);
    if (opts?.dateTo) p.set('dateTo', opts.dateTo);
    if (opts?.category && opts.category !== 'all') p.set('category', opts.category);
    if (opts?.eventType && opts.eventType !== 'all') p.set('eventType', opts.eventType);
    if (opts?.enrichmentStatus && opts.enrichmentStatus !== 'all') p.set('enrichmentStatus', opts.enrichmentStatus);
    if (opts?.userCountry && opts.userCountry !== 'all') p.set('userCountry', opts.userCountry);
    if (opts?.memberCodes) p.set('memberCode', opts.memberCodes);
    if (opts?.showExcluded) p.set('showExcluded', opts.showExcluded);
    const qs = p.toString();
    return `/api/admin/captured-ads/export${qs ? `?${qs}` : ''}`;
  },

  simulateCapturedAdClick: (id: number): Promise<{ finalUrl: string }> =>
    fetchAPI(`/api/admin/captured-ads/${id}/simulate-click`, {
      method: 'POST',
    }),

  getSystemOverview: (): Promise<SystemOverview> =>
    fetchAPI('/api/admin/system-overview'),

  getSystemHealth: (): Promise<SystemHealth> =>
    fetchAPI('/api/admin/system-health'),

  getSystemOverviewHistory: (): Promise<SystemOverviewHistory> =>
    fetchAPI('/api/admin/system-overview/history'),

  getDetectionRules: (): Promise<any> =>
    fetchAPI('/api/admin/detection-rules'),

  getMemberLoginHistory: (code: string, limit = 50): Promise<MemberLoginHistory[]> =>
    fetchAPI(`/api/admin/member-login-history/${code}?limit=${limit}`),

  getLatestApk: (): Promise<any> =>
    fetchAPI('/api/admin/apk/latest'),

  getApkList: (): Promise<any> =>
    fetchAPI('/api/admin/apk/list'),

  deleteApk: (id: number): Promise<{ success: boolean }> =>
    fetchAPI(`/api/admin/apk/${id}`, { method: 'DELETE' }),

  uploadApk: async (file: File, version: string, notes?: string): Promise<any> => {
    const formData = new FormData();
    formData.append('apk', file);
    formData.append('version', version);
    if (notes) formData.append('notes', notes);

    const response = await fetch(`${API_BASE}/api/admin/apk/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.json();
  },
};

// Research Analytics APIs
interface ResearchParams {
  dateFrom?: string;
  dateTo?: string;
  platform?: string;
  cohort?: string;
  category?: string;
  memberCode?: string;
  country?: string;
}
function buildResearchParams(opts?: ResearchParams) {
  const p = new URLSearchParams();
  if (opts?.dateFrom) p.set('dateFrom', opts.dateFrom);
  if (opts?.dateTo) p.set('dateTo', opts.dateTo);
  if (opts?.platform && opts.platform !== 'all') p.set('platform', opts.platform);
  if (opts?.cohort && opts.cohort !== 'all') p.set('cohort', opts.cohort);
  if (opts?.category && opts.category !== 'all') p.set('category', opts.category);
  if (opts?.memberCode && opts.memberCode !== 'all') p.set('memberCode', opts.memberCode);
  if (opts?.country && opts.country !== 'all') p.set('country', opts.country);
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

export const researchAPI = {
  getOverview: (opts?: ResearchParams): Promise<ResearchOverview> =>
    fetchAPI(`/api/admin/research/overview${buildResearchParams(opts)}`),

  getGamblingExposure: (opts?: ResearchParams): Promise<GamblingExposure[]> =>
    fetchAPI(`/api/admin/research/gambling-exposure${buildResearchParams(opts)}`),

  getLegalStatus: (opts?: ResearchParams): Promise<LegalStatusDaily[]> =>
    fetchAPI(`/api/admin/research/legal-status${buildResearchParams(opts)}`),

  getTemporalHeatmap: (opts?: ResearchParams & { gamblingOnly?: boolean }): Promise<TemporalHeatmapCell[]> => {
    const p = new URLSearchParams();
    if (opts?.dateFrom) p.set('dateFrom', opts.dateFrom);
    if (opts?.dateTo) p.set('dateTo', opts.dateTo);
    if (opts?.platform && opts.platform !== 'all') p.set('platform', opts.platform);
    if (opts?.memberCode && opts.memberCode !== 'all') p.set('memberCode', opts.memberCode);
    if (opts?.country && opts.country !== 'all') p.set('country', opts.country);
    if (opts?.gamblingOnly) p.set('gamblingOnly', 'true');
    const qs = p.toString();
    return fetchAPI(`/api/admin/research/temporal-heatmap${qs ? `?${qs}` : ''}`);
  },

  getPlatformComparison: (opts?: ResearchParams): Promise<PlatformComparison[]> =>
    fetchAPI(`/api/admin/research/platform-comparison${buildResearchParams(opts)}`),

  getAdvertiserProfiles: (opts?: ResearchParams): Promise<AdvertiserProfile[]> =>
    fetchAPI(`/api/admin/research/advertiser-profiles${buildResearchParams(opts)}`),

  getExposureDistribution: (opts?: ResearchParams): Promise<ExposureDistribution> =>
    fetchAPI(`/api/admin/research/exposure-distribution${buildResearchParams(opts)}`),

  getCategoryCooccurrence: (opts?: ResearchParams): Promise<CategoryCooccurrence[]> =>
    fetchAPI(`/api/admin/research/category-cooccurrence${buildResearchParams(opts)}`),

  getWeeklyTrends: (opts?: ResearchParams): Promise<WeeklyTrend[]> =>
    fetchAPI(`/api/admin/research/weekly-trends${buildResearchParams(opts)}`),

  getParticipation: (opts?: ResearchParams): Promise<ParticipationData> =>
    fetchAPI(`/api/admin/research/participation${buildResearchParams(opts)}`),

  getGamblingGallery: (opts?: ResearchParams & { limit?: number; offset?: number }): Promise<any> => {
    const p = new URLSearchParams();
    if (opts?.category && opts.category !== 'all') p.set('category', opts.category);
    if (opts?.platform && opts.platform !== 'all') p.set('platform', opts.platform);
    if (opts?.limit) p.set('limit', String(opts.limit));
    if (opts?.offset) p.set('offset', String(opts.offset));
    if (opts?.dateFrom) p.set('dateFrom', opts.dateFrom);
    if (opts?.dateTo)   p.set('dateTo',   opts.dateTo);
    if (opts?.memberCode && opts.memberCode !== 'all') p.set('memberCode', opts.memberCode);
    const qs = p.toString();
    return fetchAPI(`/api/admin/research/gambling-gallery${qs ? `?${qs}` : ''}`);
  },

  getClassificationAccuracy: (): Promise<any> =>
    fetchAPI('/api/admin/research/classification-accuracy'),

  getDemographics: (): Promise<any> =>
    fetchAPI('/api/admin/research/demographics'),

  setCohort: (code: string, cohortGroup: string): Promise<any> =>
    fetchAPI(`/api/admin/research/demographics/${code}/cohort`, {
      method: 'PUT',
      body: JSON.stringify({ cohortGroup }),
    }),

  bulkSetCohort: (codes: string[], cohortGroup: string): Promise<any> =>
    fetchAPI('/api/admin/research/demographics/bulk-cohort', {
      method: 'POST',
      body: JSON.stringify({ codes, cohortGroup }),
    }),

  getCohorts: (): Promise<any[]> =>
    fetchAPI('/api/admin/research/cohorts'),

  getActivityTimeline: (opts?: { dateFrom?: string; dateTo?: string; codes?: string[]; country?: string }): Promise<ActivityTimeline> => {
    const p = new URLSearchParams();
    if (opts?.dateFrom) p.set('dateFrom', opts.dateFrom);
    if (opts?.dateTo) p.set('dateTo', opts.dateTo);
    if (opts?.codes?.length) p.set('codes', opts.codes.join(','));
    if (opts?.country && opts.country !== 'all') p.set('country', opts.country);
    const qs = p.toString();
    return fetchAPI(`/api/admin/research/activity-timeline${qs ? `?${qs}` : ''}`);
  },
};

// Research Export APIs
export const exportAPI = {
  aggregatedUrl: (opts?: { dateFrom?: string; dateTo?: string; anonymize?: boolean }): string => {
    const p = new URLSearchParams();
    if (opts?.dateFrom) p.set('dateFrom', opts.dateFrom);
    if (opts?.dateTo) p.set('dateTo', opts.dateTo);
    if (opts?.anonymize) p.set('anonymize', 'true');
    const qs = p.toString();
    return `/api/admin/export/aggregated${qs ? `?${qs}` : ''}`;
  },

  getStatisticalSummary: (opts?: ResearchParams): Promise<any> =>
    fetchAPI(`/api/admin/export/statistical-summary${buildResearchParams(opts)}`),

  gamblingDetailUrl: (opts?: { dateFrom?: string; dateTo?: string; category?: string }): string => {
    const p = new URLSearchParams();
    if (opts?.dateFrom) p.set('dateFrom', opts.dateFrom);
    if (opts?.dateTo) p.set('dateTo', opts.dateTo);
    if (opts?.category && opts.category !== 'all') p.set('category', opts.category);
    const qs = p.toString();
    return `/api/admin/export/gambling-detail${qs ? `?${qs}` : ''}`;
  },

  dailyActivityUrl: (opts?: { date?: string; cohort?: string }): string => {
    const p = new URLSearchParams();
    if (opts?.date) p.set('date', opts.date);
    if (opts?.cohort) p.set('cohort', opts.cohort);
    const qs = p.toString();
    return `/api/admin/export/daily-activity${qs ? `?${qs}` : ''}`;
  },
};

// Report APIs (2026-06-26 Kido No.2 daily summary + No.3 tester access). Aggregate JSON, JST.
function rangeQs(opts?: { dateFrom?: string; dateTo?: string }): string {
  const p = new URLSearchParams();
  if (opts?.dateFrom) p.set('dateFrom', opts.dateFrom);
  if (opts?.dateTo) p.set('dateTo', opts.dateTo);
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}
export const reportAPI = {
  dailySummary: (opts?: { dateFrom?: string; dateTo?: string }): Promise<DailySummaryReport> =>
    fetchAPI(`/api/admin/export/report-daily-summary${rangeQs(opts)}`),
  testerAccess: (opts?: { dateFrom?: string; dateTo?: string }): Promise<TesterAccessReport> =>
    fetchAPI(`/api/admin/export/report-tester-access${rangeQs(opts)}`),
};

// Member Demographics APIs
export const memberDemographicsAPI = {
  get: (): Promise<MemberDemographics | null> =>
    fetchAPI('/api/member/demographics'),

  submit: (data: { ageRange?: string; gender?: string; dailySnsHours?: string; primaryPlatform?: string; prefecture?: string }): Promise<any> =>
    fetchAPI('/api/member/demographics', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  submitConsent: (data: { consentVersion?: string; consentType?: string; irbProtocolId?: string }): Promise<any> =>
    fetchAPI('/api/member/consent', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ---- Post-Survey Analysis (sasadmin-only; higher-accuracy corrected layer) ----
export const postAnalysisAPI = {
  getOverview: (): Promise<PsaOverview> => fetchAPI('/api/admin/post-analysis/overview'),
  getCategories: (): Promise<PsaCategory[]> => fetchAPI('/api/admin/post-analysis/categories'),
  getTemporal: (): Promise<PsaTemporal> => fetchAPI('/api/admin/post-analysis/temporal'),
  getDaily: (): Promise<PsaDailyPoint[]> => fetchAPI('/api/admin/post-analysis/daily'),
  getDemographics: (): Promise<PsaDemographics> => fetchAPI('/api/admin/post-analysis/demographics'),
  getAdvertisers: (genre?: string): Promise<PsaAdvertiser[]> =>
    fetchAPI(`/api/admin/post-analysis/advertisers${genre && genre !== 'all' ? `?genre=${encodeURIComponent(genre)}` : ''}`),
  getReviewQueue: (): Promise<PsaReviewItem[]> => fetchAPI('/api/admin/post-analysis/review-queue'),
  getSource: (params: Record<string, string | number | boolean | undefined>): Promise<PsaSourceResult> => {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') p.set(k, String(v)); });
    return fetchAPI(`/api/admin/post-analysis/source?${p.toString()}`);
  },
  getNo4PerMember: (): Promise<PsaNo4PerMember> => fetchAPI('/api/admin/post-analysis/no4-per-member'),
  getNo4TypeMix: (): Promise<PsaNo4TypeMix> => fetchAPI('/api/admin/post-analysis/no4-type-mix'),
  getNo4Parity: (): Promise<PsaNo4Parity> => fetchAPI('/api/admin/post-analysis/no4-parity'),
  // 2026-08-07 additional analysis report (No.1-No.4)
  getReport0807: (): Promise<PsaReport0807> => fetchAPI('/api/admin/post-analysis/report0807'),
  getReport0807Parity: (): Promise<PsaReport0807Parity> => fetchAPI('/api/admin/post-analysis/report0807-parity'),
  getRuns: (): Promise<PsaRun[]> => fetchAPI('/api/admin/post-analysis/runs'),
  rebuild: (from?: string, to?: string): Promise<{ success: boolean; runId: number; summary: any }> =>
    fetchAPI('/api/admin/post-analysis/rebuild', { method: 'POST', body: JSON.stringify({ from, to }) }),
};

// ---- Test/survey periods (setting; read = any admin, write = sasadmin) --------
export const testPeriodsAPI = {
  list: (): Promise<TestPeriod[]> => fetchAPI('/api/admin/test-periods'),
  getCurrent: (): Promise<TestPeriod | null> => fetchAPI('/api/admin/test-periods/current'),
  create: (label: string, from: string, to: string, setCurrent = true): Promise<{ success: boolean; period: TestPeriod }> =>
    fetchAPI('/api/admin/test-periods', { method: 'POST', body: JSON.stringify({ label, from, to, setCurrent }) }),
  setCurrent: (id: number): Promise<{ success: boolean }> =>
    fetchAPI(`/api/admin/test-periods/${id}/current`, { method: 'PUT' }),
  remove: (id: number): Promise<{ success: boolean }> =>
    fetchAPI(`/api/admin/test-periods/${id}`, { method: 'DELETE' }),
};

// Ad display-time verification (X, 2026-10-01) — sasadmin only.
export const displayTimeAPI = {
  getXVerification: (): Promise<import('../types').DtvXVerification> =>
    fetchAPI('/api/admin/display-time/x-verification'),
  getLive: (q: { days: number; platform?: string; includeTest?: boolean; gamblingOnly?: boolean }):
    Promise<import('../types').DtLive> =>
    fetchAPI(`/api/admin/display-time/live?days=${q.days}` +
      (q.platform ? `&platform=${q.platform}` : '') +
      (q.includeTest ? '&includeTest=1' : '') + (q.gamblingOnly ? '&gamblingOnly=1' : '')),
};
